import React, { useState, useEffect } from 'react';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface Branch { id: string; name: string; city: string; }

interface Transaction {
  id: string;
  transaction_number: string;
  branch_id: string;
  branch_name?: string;
  cashier_name?: string;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  payment_method: string;
  is_voided: boolean;
  void_reason?: string;
  created_at: string;
  item_names?: string;
  items_detail?: { name: string; quantity: number; unit_price: number; subtotal: number }[];
  items?: { product_name?: string; quantity: number; unit_price: number; subtotal: number }[];
  addons?: { addon_name?: string; quantity: number; unit_price: number; subtotal: number }[];
}

interface TransactionDetail extends Transaction {}

const today = () => new Date().toISOString().slice(0, 10);

type SortKey = 'date_desc' | 'date_asc' | 'total_desc' | 'total_asc';

export const TransactionsPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [filter, setFilter] = useState({
    startDate: today(),
    endDate: today(),
    branchId: user?.role === 'admin_branch' ? (user?.branch_id || '') : '',
    method: 'all',
    item: '',
    search: '',
    sortBy: 'date_desc' as SortKey,
  });

  const [enriching, setEnriching] = useState(false);
  const [voidModal, setVoidModal] = useState<Transaction | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user) navigate('/login');
    if (['super_admin', 'finance', 'accounting'].includes(user?.role || '')) fetchBranches();
    fetchTransactions();
  }, [user, navigate]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, { headers: authHeaders });
      setBranches(res.data.data || res.data || []);
    } catch {}
  };

  // After list loads, fetch each transaction's detail to get item names
  const enrichWithItems = async (rows: Transaction[]) => {
    if (rows.length === 0) return;
    setEnriching(true);
    try {
      const BATCH = 10;
      let enriched = [...rows];
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const results = await Promise.all(
          batch.map(async (t) => {
            if (t.items !== undefined) return t; // already fully enriched
            try {
              const res = await axios.get(`${API}/transactions/${t.id}`, { headers: authHeaders });
              const d = res.data.data || {};
              const names = [
                ...(d.items || []).map((x: any) => x.product_name).filter(Boolean),
                ...(d.addons || []).map((x: any) => x.addon_name).filter(Boolean),
              ];
              const seen = new Set<string>();
              const unique = names.filter((n: string) => { if (seen.has(n)) return false; seen.add(n); return true; });
              return {
                ...t,
                item_names: unique.join(', ') || undefined,
                items: d.items || [],
                addons: d.addons || [],
              };
            } catch { return t; }
          })
        );
        results.forEach((r, idx) => { enriched[i + idx] = r; });
        setTransactions([...enriched]);
      }
    } finally {
      setEnriching(false);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        start_date: filter.startDate,
        end_date: filter.endDate,
        limit: '500',
      };
      const branchId = user?.role === 'admin_branch' ? user?.branch_id : filter.branchId;
      if (branchId) params.branch_id = branchId;
      if (filter.item.trim()) params.item = filter.item.trim();

      const response = await axios.get(`${API}/transactions`, { headers: authHeaders, params });
      const rows = (response.data.data || []).map((t: any) => ({
        ...t,
        subtotal: parseFloat(t.subtotal) || 0,
        discount_amount: parseFloat(t.discount_amount) || 0,
        tax_amount: parseFloat(t.tax_amount) || 0,
        total_amount: parseFloat(t.total_amount) || 0,
      }));
      setTransactions(rows);
      enrichWithItems(rows);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!voidModal) return;
    setVoiding(true); setVoidError('');
    try {
      await axios.post(`${API}/transactions/${voidModal.id}/void`,
        { void_reason: voidReason, voided_by: user?.id },
        { headers: authHeaders }
      );
      setVoidModal(null); setVoidReason('');
      fetchTransactions();
    } catch (err: any) {
      setVoidError(err.response?.data?.error || err.message || 'Gagal void transaksi');
    } finally { setVoiding(false); }
  };

  // Fetch per-transaction detail for 1NF export
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const details: TransactionDetail[] = await Promise.all(
        filteredTransactions.map(async (t) => {
          try {
            const res = await axios.get(`${API}/transactions/${t.id}`, { headers: authHeaders });
            return { ...t, ...(res.data.data || {}) };
          } catch { return t; }
        })
      );

      // Build 1NF rows: one row per item/addon
      const rows: any[] = [];
      for (const t of details) {
        const base = {
          'No. Transaksi': t.transaction_number,
          'Tanggal': new Date(t.created_at).toLocaleString('id-ID'),
          'Cabang': t.branch_name || '',
          'Kasir': t.cashier_name || '',
          'Semua Item': t.item_names || '',
          'Total Transaksi': Number(t.total_amount),
          'Metode Bayar': t.payment_method,
          'Status': t.is_voided ? 'Void' : 'Selesai',
        };

        const allItems = [
          ...(t.items || []).map((i) => ({
            'Nama Item': i.product_name || '—',
            'Qty': Number(i.quantity),
            'Harga Satuan': Number(i.unit_price),
            'Subtotal Item': Number(i.subtotal),
          })),
          ...(t.addons || []).map((a) => ({
            'Nama Item': a.addon_name || '—',
            'Qty': Number(a.quantity),
            'Harga Satuan': Number(a.unit_price),
            'Subtotal Item': Number(a.subtotal),
          })),
        ];

        if (allItems.length === 0) {
          rows.push({ ...base, 'Nama Item': '—', 'Qty': '', 'Harga Satuan': '', 'Subtotal Item': '' });
        } else {
          allItems.forEach((item) => rows.push({ ...base, ...item }));
        }
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 24 }, { wch: 20 }, { wch: 18 }, { wch: 16 },
        { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 20 },
        { wch: 8 }, { wch: 14 }, { wch: 14 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Transaksi Detail');
      XLSX.writeFile(wb, `Transaksi_${filter.startDate}_sd_${filter.endDate}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Filter + sort client-side
  const itemQuery = filter.item.trim().toLowerCase();

  const filteredTransactions = (() => {
    // If item filter active and enrichment still running → hold, show loading
    if (itemQuery && enriching) return [];

    let list = (Array.isArray(transactions) ? transactions : []).filter((t) => {
      if (filter.method !== 'all' && t.payment_method !== filter.method) return false;

      if (itemQuery) {
        if (!(t.item_names || '').toLowerCase().includes(itemQuery)) return false;
      }

      if (filter.search) {
        const q = filter.search.toLowerCase();
        const hit =
          t.transaction_number.toLowerCase().includes(q) ||
          (t.branch_name || '').toLowerCase().includes(q) ||
          (t.cashier_name || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      switch (filter.sortBy) {
        case 'date_asc': return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'date_desc': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'total_desc': return b.total_amount - a.total_amount;
        case 'total_asc': return a.total_amount - b.total_amount;
        default: return 0;
      }
    });
    return list;
  })();

  const nonVoided = filteredTransactions.filter((t) => !t.is_voided);
  const stats = {
    total: nonVoided.length,
    totalRevenue: nonVoided.reduce((sum, t) => sum + (t.total_amount || 0), 0),
    voided: filteredTransactions.filter((t) => t.is_voided).length,
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>💳 Transaksi</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Daftar transaksi individual · <strong>Reports</strong> = ringkasan & grafik per periode
          </p>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Total Transaksi</p>
          <h3 style={styles.statValue}>{stats.total}</h3>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Total Pendapatan</p>
          <h3 style={styles.statValue}>Rp{stats.totalRevenue.toLocaleString('id-ID')}</h3>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Void</p>
          <h3 style={{ ...styles.statValue, color: '#e74c3c' }}>{stats.voided}</h3>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Rata-rata</p>
          <h3 style={styles.statValue}>
            Rp{stats.total > 0 ? Math.round(stats.totalRevenue / stats.total).toLocaleString('id-ID') : 0}
          </h3>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterContainer}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Dari Tanggal</label>
            <input type="date" value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
              style={styles.input} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Sampai Tanggal</label>
            <input type="date" value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
              style={styles.input} />
          </div>
          {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Cabang</label>
              <select value={filter.branchId}
                onChange={(e) => setFilter({ ...filter, branchId: e.target.value })}
                style={styles.input}>
                <option value="">Semua Cabang</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Metode Bayar</label>
            <select value={filter.method}
              onChange={(e) => setFilter({ ...filter, method: e.target.value })}
              style={styles.input}>
              <option value="all">Semua Metode</option>
              <option value="cash">Tunai</option>
              <option value="qris">QRIS</option>
              <option value="transfer">Transfer</option>
              <option value="multiple">Campuran</option>
              <option value="free">Gratis</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Urutkan</label>
            <select value={filter.sortBy}
              onChange={(e) => setFilter({ ...filter, sortBy: e.target.value as SortKey })}
              style={styles.input}>
              <option value="date_desc">Terbaru</option>
              <option value="date_asc">Terlama</option>
              <option value="total_desc">Total Terbesar</option>
              <option value="total_asc">Total Terkecil</option>
            </select>
          </div>
          <div style={{ ...styles.filterGroup, flex: 2 }}>
            <label style={styles.filterLabel}>🛒 Filter Item / Add-on</label>
            <input type="text" value={filter.item}
              onChange={(e) => setFilter({ ...filter, item: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && fetchTransactions()}
              placeholder="Contoh: Raket, Air Mineral..."
              style={{ ...styles.input, borderColor: filter.item ? '#005bbf' : '#d1d5db' }} />
          </div>
          <div style={{ ...styles.filterGroup, flex: 2 }}>
            <label style={styles.filterLabel}>Cari (No. / Cabang / Kasir)</label>
            <input type="text" value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              placeholder="Cari transaksi..."
              style={styles.input} />
          </div>
        </div>
        <div style={styles.filterActions}>
          <button onClick={fetchTransactions} style={styles.searchBtn} disabled={loading}>
            {loading ? '⏳ Memuat...' : '🔍 Cari'}
          </button>
          <button onClick={handleExportExcel} style={styles.exportBtn}
            disabled={exporting || filteredTransactions.length === 0}>
            {exporting ? '⏳ Menyiapkan...' : '📥 Export Excel (Detail Item)'}
          </button>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>
          Filter Item dikirim ke server (cari transaksi yang berisi item tersebut) · Export = satu baris per item (format 1NF)
        </p>
      </div>

      {/* Void Modal */}
      {voidModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 10, padding: 24, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, color: '#e74c3c' }}>🚫 Void Transaksi</h3>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: 13 }}>
              {voidModal.transaction_number} · Rp{voidModal.total_amount.toLocaleString('id-ID')}
            </p>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
              Alasan Void <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)}
              placeholder="Masukkan alasan void transaksi..."
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minHeight: 80, boxSizing: 'border-box', resize: 'vertical' }} />
            {voidError && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
                {voidError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleVoid} disabled={voiding || !voidReason.trim()}
                style={{ flex: 1, padding: '10px 0', background: voiding || !voidReason.trim() ? '#ccc' : '#e74c3c', color: 'white', border: 'none', borderRadius: 6, cursor: voiding || !voidReason.trim() ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}>
                {voiding ? 'Memproses...' : '🚫 Konfirmasi Void'}
              </button>
              <button onClick={() => { setVoidModal(null); setVoidError(''); }}
                style={{ padding: '10px 20px', background: '#f5f5f5', color: '#555', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>⏳ Memuat transaksi...</div>
        ) : filteredTransactions.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <p style={{ fontSize: 16, margin: 0 }}>Tidak ada transaksi pada periode ini</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>No. Transaksi</th>
                <th style={styles.th}>Tanggal</th>
                {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && <th style={styles.th}>Cabang</th>}
                <th style={styles.th}>Kasir</th>
                <th style={{ ...styles.th, minWidth: 160 }}>Item</th>
                <th style={styles.th}>Subtotal</th>
                <th style={styles.th}>Diskon</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Metode</th>
                <th style={styles.th}>Status</th>
                {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && <th style={styles.th}>Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((trx) => (
                <tr key={trx.id} style={{ ...styles.row, background: trx.is_voided ? '#ffebee' : 'white', opacity: trx.is_voided ? 0.6 : 1 }}>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{trx.transaction_number}</td>
                  <td style={styles.td}>{new Date(trx.created_at).toLocaleString('id-ID')}</td>
                  {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && <td style={styles.td}>{trx.branch_name || '—'}</td>}
                  <td style={styles.td}>{trx.cashier_name || '—'}</td>
                  <td style={{ ...styles.td, maxWidth: 200, fontSize: 12, color: '#475569' }}>
                    {trx.item_names ? (
                      <span>{trx.item_names.split(', ').length} item</span>
                    ) : enriching ? (
                      <span style={{ color: '#94a3b8' }}>⏳</span>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>—</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, minWidth: 200 }}>
                    {(() => {
                      const rows = trx.items_detail && trx.items_detail.length > 0
                        ? trx.items_detail
                        : [
                            ...(trx.items || []).map((i: any) => ({ name: i.product_name || '—', quantity: i.quantity, unit_price: i.unit_price, subtotal: i.subtotal })),
                            ...(trx.addons || []).map((a: any) => ({ name: a.addon_name || '—', quantity: a.quantity, unit_price: a.unit_price, subtotal: a.subtotal })),
                          ];
                      if (rows.length === 0) return <span>Rp{(trx.subtotal || 0).toLocaleString('id-ID')}</span>;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {rows.map((row: any, i: number) => (
                            <div key={i} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <span style={{ color: '#64748b' }}>
                                {row.quantity}× <span style={{ color: '#374151', fontWeight: 600 }}>{row.name}</span>
                              </span>
                              <span style={{ fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
                                Rp{Number(row.subtotal || 0).toLocaleString('id-ID')}
                              </span>
                            </div>
                          ))}
                          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 2, paddingTop: 2, fontSize: 12, fontWeight: 700, color: '#0f172a', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total</span>
                            <span>Rp{(trx.subtotal || 0).toLocaleString('id-ID')}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td style={styles.td}>
                    {(trx.discount_amount || 0) > 0 ? `-Rp${(trx.discount_amount || 0).toLocaleString('id-ID')}` : '-'}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>Rp{(trx.total_amount || 0).toLocaleString('id-ID')}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: getPaymentColor(trx.payment_method) }}>
                      {trx.payment_method === 'free' ? '🎁 Gratis' : trx.payment_method}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: trx.is_voided ? '#e74c3c' : '#27ae60' }}>
                      {trx.is_voided ? 'Void' : 'Selesai'}
                    </span>
                  </td>
                  {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && (
                    <td style={styles.td}>
                      {!trx.is_voided && (
                        <button onClick={() => { setVoidModal(trx); setVoidReason(''); setVoidError(''); }}
                          style={{ padding: '4px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          Void
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ textAlign: 'right', marginTop: 8, fontSize: 13, color: '#94a3b8', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        {enriching && (
          <span style={{ fontSize: 12, color: '#64748b' }}>⏳ Memuat nama item...</span>
        )}
        <span>{filteredTransactions.length} transaksi ditampilkan</span>
      </div>
    </div>
  );
};

const getPaymentColor = (method: string) => {
  switch (method) {
    case 'cash': return '#27ae60';
    case 'qris': return '#667eea';
    case 'transfer': return '#f39c12';
    case 'card': return '#e74c3c';
    case 'free': return '#e67e22';
    default: return '#95a5a6';
  }
};

const styles = {
  container: { padding: '20px', background: '#f5f5f5', minHeight: '100vh' },
  header: { background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' },
  statCard: { background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  statLabel: { margin: 0, color: '#666', fontSize: '12px' },
  statValue: { margin: '10px 0 0 0', fontSize: '24px', color: '#333' },
  filterContainer: { background: 'white', padding: '16px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  filterRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' as const, marginBottom: '12px' },
  filterGroup: { display: 'flex', flexDirection: 'column' as const, flex: 1, minWidth: '130px' },
  filterLabel: { fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#374151', background: '#fff', outline: 'none' },
  filterActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' as const },
  searchBtn: { padding: '9px 20px', background: '#005bbf', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 },
  exportBtn: { padding: '9px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  headerRow: { background: '#f9f9f9' },
  th: { padding: '12px 15px', textAlign: 'left' as const, fontWeight: '600' as const, borderBottom: '2px solid #e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' as const },
  row: { borderBottom: '1px solid #e0e0e0' },
  td: { padding: '12px 15px', fontSize: '13px' },
  badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '20px', color: 'white', fontSize: '11px' },
};
